import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    HostListener,
    OnDestroy,
    TemplateRef,
    ViewChild,
    inject,
    ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import moment, { Moment } from 'moment';
import { Observable, OperatorFunction, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
    HeaderContentService,
    MessageType,
    NotificationService,
    PauseKind,
    PausedEntry,
    PurchaseOrder,
    PurchaseOrderLine,
    PurchaseOrderService,
    PurchaseOrdersDoc,
    RopOverrideService,
    UnifiedQueryV2Service,
    UtilService,
    sumOpenOrderQty,
} from 'src/app/shared/services';
import {
    EoqRow,
    ORDERABLE_PRODUCTS_QUERY,
    ReorderingConfig,
    ReorderingService,
    RopResponse,
} from 'src/app/shared/services/reordering.service';
import {
    AbcAnalysisResponse,
    AbcAnalysisService,
} from 'src/app/shared/services/abc-analysis.service';
import {
    EMPTY_SUPPLIER_INDEX,
    EMPTY_SUPPLIER_OVERRIDES,
    NO_SUPPLIER_KEY,
    OrderSupplierView,
    Supplier1688Index,
    Supplier1688Row,
    SupplierOverridesDoc,
    buildOrderSupplierView,
    buildSupplierIndex,
} from './supplier-1688';
import { Supplier1688Service } from './supplier-1688.service';
import { ColumnViewsPopupComponent } from './column-views-popup/column-views-popup.component';
import {
    ColumnViewSelection,
    ColumnViewsDoc,
    ColumnViewsService,
    ORDERING_ONLY_COLUMNS,
    isColumnVisible,
} from './column-views';
import { Supplier1688EditorComponent } from './supplier-1688-editor/supplier-1688-editor.component';
import { Supplier1688LinksComponent } from './supplier-1688-links/supplier-1688-links.component';

import { exportDataGrid } from 'devextreme/excel_exporter';
import { confirm } from 'devextreme/ui/dialog';
import { Workbook } from 'exceljs';
import * as FileSaver from 'file-saver';
import {
    isEmpty as _isEmpty,
    isEqual as _isEqual,
    merge as _merge,
    keyBy as _keyBy,
    uniq as _uniq,
    range as _range,
    countBy as _countBy,
    filter as _filter,
    isNil as _isNil,
    get as _get,
    set as _set,
    each as _each,
    map as _map,
    pick as _pick,
    includes as _includes,
    sumBy as _sumBy,
    orderBy as _orderBy,
    truncate as _truncate,
} from 'lodash';
import { DxNumberBoxComponent } from 'devextreme-angular/ui/number-box';
import { DxCheckBoxComponent } from 'devextreme-angular/ui/check-box';
import { DxButtonComponent } from 'devextreme-angular/ui/button';
import { DxButtonGroupComponent } from 'devextreme-angular/ui/button-group';
import { DxDropDownButtonComponent } from 'devextreme-angular/ui/drop-down-button';
import { DxPopupComponent } from 'devextreme-angular/ui/popup';
import { DxSelectBoxComponent } from 'devextreme-angular/ui/select-box';
import { DxScrollViewComponent } from 'devextreme-angular/ui/scroll-view';
import { DxSwitchComponent } from 'devextreme-angular/ui/switch';
import { DxTextBoxComponent } from 'devextreme-angular/ui/text-box';
import { DxTooltipComponent } from 'devextreme-angular/ui/tooltip';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';
import {
    DxoFilterPanelComponent,
    DxoFilterBuilderComponent,
    DxoFilterBuilderPopupComponent,
    DxoPagingComponent,
    DxoPagerComponent,
    DxoFilterRowComponent,
    DxoStateStoringComponent,
    DxoEditingComponent,
    DxiColumnComponent,
} from 'devextreme-angular/ui/nested';
import { DxTemplateDirective } from 'devextreme-angular/core';
import { FormatRelativeDatetimePipe } from '../../shared/pipes/format-relative-datetime.pipe';
import { FormatNumberPipe } from '../../shared/pipes/format-number.pipe';

// Built-in per-class ROP service levels ("Other" = C/D/N/unclassified);
// also reapplied when a level is cleared in the UI or a persisted value is
// out of range.
const DEFAULT_SERVICE_LEVELS = { A: 0.9, B: 0.7, other: 0.5 } as const;
// Built-in values for the remaining settings, with the same bounds the inputs
// enforce. Reapplied the same way: a cleared number box yields null, and a
// null here would reach the lambdas (calculationDays: null collapses the
// snapshot window to today) or zero out a whole column (demand × null).
const DEFAULT_SETTINGS = {
    calculationDays: 90,
    reorderingLeadTimeDays: 21,
    demandForDays: 30,
    orderingCost: 150,
    holdingRate: 0.25,
    costRatio: 0.6,
} as const;
const SETTING_BOUNDS: Record<
    keyof typeof DEFAULT_SETTINGS,
    readonly [number, number]
> = {
    calculationDays: [30, 360],
    reorderingLeadTimeDays: [2, 360],
    demandForDays: [2, 360],
    orderingCost: [1, Infinity],
    holdingRate: [0.01, Infinity],
    costRatio: [0.01, 1],
};
// Everything the settings popup edits — what openConfig snapshots and a
// dismissed popup puts back.
const POPUP_SETTING_FIELDS = [
    'serviceLevelA',
    'serviceLevelB',
    'serviceLevelOther',
    'orderingCost',
    'holdingRate',
    'costRatio',
] as const;
type PopupSettings = Record<(typeof POPUP_SETTING_FIELDS)[number], number>;
// Trailing debounce for window resize — a dragged window edge fires many
// events per second, and each refit can reload the grid.
const RESIZE_DEBOUNCE_MS = 150;
// Valid service-level range, mirroring the popup input bounds. The ROP lambda
// itself accepts the open interval (0,1), but below 0.5 would mean negative
// safety stock.
const SERVICE_LEVEL_MIN = 0.5;
const SERVICE_LEVEL_MAX = 0.999;
// localStorage key for the persisted grid column layout (widths and sort; which
// columns show comes from the column view).
const GRID_STATE_STORAGE_KEY = 'reordering-grid-state';
// localStorage key for the in-progress Order Qty draft, as { pid: qty }.
// Deliberately NOT part of the purchase-orders document: a draft is working
// state, and autosaving it into that document would put a write on every
// keystroke in the same slot as real order/pause writes, where the loser of
// a race silently drops an order.
const ORDER_DRAFT_STORAGE_KEY = 'reordering-order-draft';
// Excel number formats for the export, by dataField. Cell templates/pipes
// only shape the on-screen text, so without an explicit numFmt the raw float
// precision would leak into the .xlsx.
const EXPORT_NUM_FORMATS: Record<string, string> = {
    'price.$numberDecimal': '#,##0.00',
    total_sale: '#,##0.00',
    demand: '#,##0',
    remain: '#,##0',
    stock_cover_days: '#,##0',
    turnover: '#,##0.00',
    avg_stock: '#,##0.0',
    snapshot_days: '#,##0',
    rop: '#,##0.00',
    eoq_units: '#,##0',
    unit_sold: '#,##0',
    data_days: '#,##0.0',
    demand_std_dev: '#,##0.00',
    safety_stock: '#,##0.00',
    sale_share: '0.00%',
    annual_demand: '#,##0',
    unit_value: '#,##0.00',
    annual_holding_cost_per_unit: '#,##0.00',
    eoq: '#,##0.00',
    orders_per_year: '#,##0.00',
    order_cycle_days: '#,##0',
    total_annual_variable_cost: '#,##0.00',
    on_order_qty: '#,##0',
    order_qty: '#,##0',
    projected_total: '#,##0',
    'supplier.costCny': '#,##0.00',
};
// Columns that exist only for ordering mode — never persisted by the grid
// state storing (see saveGridState / loadGridState).
// 'buttons' was the command column holding the per-row pause buttons, now a
// right-click menu instead; it stays listed so a layout saved by an older
// build can't resurrect a phantom column entry.
const ORDERING_MODE_COLUMNS = [...ORDERING_ONLY_COLUMNS, 'buttons'];

// An entry of the short-term pause menu: one paused product, or the flagged
// "Restore all" header (pid: null there).
interface PausedMenuItem {
    pid: number | null;
    text: string;
    restoreAll?: boolean;
}

@Component({
    selector: 'app-reordering',
    templateUrl: './reordering.component.html',
    styleUrls: ['./reordering.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DxNumberBoxComponent,
        DxCheckBoxComponent,
        DxButtonComponent,
        DxButtonGroupComponent,
        DxDropDownButtonComponent,
        DxTooltipComponent,
        DxPopupComponent,
        DxDataGridComponent,
        DxoFilterPanelComponent,
        DxoFilterBuilderComponent,
        DxoFilterBuilderPopupComponent,
        DxoPagingComponent,
        DxoPagerComponent,
        DxoFilterRowComponent,
        DxoStateStoringComponent,
        DxoEditingComponent,
        DxSelectBoxComponent,
        ColumnViewsPopupComponent,
        Supplier1688EditorComponent,
        Supplier1688LinksComponent,
        DxTextBoxComponent,
        DxScrollViewComponent,
        DxSwitchComponent,
        DxTemplateDirective,
        DxiColumnComponent,
        FormatRelativeDatetimePipe,
        FormatNumberPipe,
    ],
})
export class ReorderingComponent implements AfterViewInit, OnDestroy {
    private cdref = inject(ChangeDetectorRef);
    private destroyRef = inject(DestroyRef);
    private notificationService = inject(NotificationService);
    private reorderingService = inject(ReorderingService);
    private unifiedQueryV2Service = inject(UnifiedQueryV2Service);
    private abcAnalysisService = inject(AbcAnalysisService);
    private utilService = inject(UtilService);
    private headerContentService = inject(HeaderContentService);
    private purchaseOrderService = inject(PurchaseOrderService);
    private ropOverrideService = inject(RopOverrideService);
    private supplier1688Service = inject(Supplier1688Service);
    private columnViewsService = inject(ColumnViewsService);
    private router = inject(Router);

    @ViewChild('dataGrid', { static: true })
    private dataGrid!: DxDataGridComponent;

    @ViewChild('columnViewsPopup', { static: true })
    private columnViewsPopup!: ColumnViewsPopupComponent;

    @ViewChild('supplierEditor', { static: true })
    private supplierEditor!: Supplier1688EditorComponent;

    // Action buttons projected into the app header's center slot.
    @ViewChild('headerMenu', { static: true })
    private headerMenu!: TemplateRef<unknown>;

    calculationDays: number = DEFAULT_SETTINGS.calculationDays;
    reorderingLeadTimeDays: number = DEFAULT_SETTINGS.reorderingLeadTimeDays;
    demandForDays: number = DEFAULT_SETTINGS.demandForDays;
    // ROP safety-stock service levels, per ABC class ("Other" = C/D/N/…;
    // 0.5 => Z=0 => no safety stock). The ROP lambda is called once per
    // distinct level and each product takes the rop from its class's response.
    serviceLevelA: number = DEFAULT_SERVICE_LEVELS.A;
    serviceLevelB: number = DEFAULT_SERVICE_LEVELS.B;
    serviceLevelOther: number = DEFAULT_SERVICE_LEVELS.other;
    // EOQ cost inputs (see temp/reordering-README.md).
    // S — fixed cost per order (THB)
    orderingCost: number = DEFAULT_SETTINGS.orderingCost;
    // i — annual holding cost as a fraction of unit value
    holdingRate: number = DEFAULT_SETTINGS.holdingRate;
    // list price -> cost basis (C = list_price * costRatio)
    costRatio: number = DEFAULT_SETTINGS.costRatio;
    dataSource: any[] | null = null;
    showOnlyBelowRop = false;
    hideDesisted = true;
    // ABC classes for the toolbar filter; empty selection = show every class.
    readonly classFilterItems = [
        { text: 'A' },
        { text: 'B' },
        { text: 'C' },
        { text: 'D' },
        { text: 'N' },
    ];
    selectedClassFilters: string[] = [];
    // Rows for the ABC tooltip table; counts/shares come from classStats.
    readonly classInfoRows = [
        {
            cls: 'A',
            desc: 'Top sellers that together make the first 80% of revenue',
        },
        { cls: 'B', desc: 'Next tier, together adding revenue up to 95%' },
        { cls: 'C', desc: 'Long tail contributing the final 5%' },
        { cls: 'D', desc: 'Dead stock: no sales in the window' },
        { cls: 'N', desc: 'New: listed within 6 months, too young to rank' },
    ];
    // Per-class row count and share of table items, shown in the ABC
    // tooltip; rebuilt on every data load from the loaded rows.
    private classStats: Record<string, { count: number; itemPct: number }> = {};
    // Description + formula per calculated column, shown as a tooltip when
    // hovering the column header (see onCellHoverChanged).
    private readonly columnDescriptions: Record<string, string> = {
        rop: 'Reorder Point — reorder when current stock ≤ ROP. ROP = avg daily demand × lead time + safety stock, where safety stock = Z(service level of the ABC class) × σ(daily demand) × √lead time. Blank inputs ⇒ 0. Double-click a cell to override the value manually (amber corner = overridden); clear it to restore the calculated ROP.',
        eoq_units:
            'Economic Order Quantity — the cost-optimal units per order, rounded up. EOQ = √(2 × D × S ÷ H); D = annual demand, S = ordering cost per order, H = annual holding cost per unit.',
        eoq: 'Raw (unrounded) EOQ = √(2 × D × S ÷ H).',
        demand: 'Expected demand over the chosen horizon: (units sold ÷ data days) × demand-for days.',
        stock_cover_days:
            'Days until stock runs out at the current demand rate: current stock ÷ avg daily demand (units sold ÷ data days). 0 = out of stock; blank = no sales in the calc period. Deliberately uses today’s stock, not the average — it answers "how long will what I have now last".',
        turnover:
            'Units-based inventory turnover, annualised from the calc-period rate: (avg daily demand × 365) ÷ average stock over the calc period (daily snapshots; products without snapshot history use today’s stock). 0 = no sales in the calc period; blank = average stock is 0.',
        avg_stock:
            'Average on-hand stock over the calc period, from the crawler’s daily stock snapshots. History begins 8 Aug 2026 — until a full period has accumulated, the average covers only the recorded days (see Snapshot Days). Products without snapshots yet show today’s stock.',
        snapshot_days:
            'Days inside the calc period with a stock snapshot backing the Avg Stock figure. Blank = no snapshots for this product yet.',
        safety_stock:
            'Buffer against demand spikes during the lead time: Z(class service level) × σ(daily demand) × √lead time. Z = 0 at 50% service level.',
        demand_std_dev:
            'σ — standard deviation of daily demand over the data days (non-selling days count as zero).',
        annual_demand: 'D — annualised demand: units sold × 365 ÷ data days.',
        data_days:
            'Effective selling span: min(calc period, max(days since listed, days since first sale)). Spans under 30 days ⇒ metrics are not computed.',
        unit_value: 'C — EOQ cost basis: list price × cost ratio.',
        annual_holding_cost_per_unit: 'H — holding rate × unit cost.',
        orders_per_year: 'Annual demand ÷ EOQ.',
        order_cycle_days: '365 ÷ orders per year.',
        total_annual_variable_cost:
            'Minimised annual ordering + holding cost: √(2 × D × S × H).',
        total_sale: 'Revenue over the last 1 year.',
        sale_share: "This product's share of total 1-year revenue.",
        class: 'ABC revenue class — hover the "ABC Class" ⓘ in the toolbar for details.',
        on_order_qty:
            'Units already on OPEN purchase orders for this product (completed orders no longer count).',
        order_qty:
            'Quantity to order now — rows with a quantity become the lines of the next saved purchase order.',
        projected_total:
            'Projected stock = current stock + on order + order quantity. Once saved, items whose projected stock reaches the ROP are hidden in ordering mode.',
        'supplier.label':
            'The option last bought on 1688; the link opens its offer page, where the option still has to be picked. ×N = one store unit is N units on 1688. "ตรวจ" = the match to this product needs a human check, hover for why. "ตั้งเอง" = set by hand, which a sync never overwrites. Fix one on the 1688 page, or right-click its row in ordering mode.',
        'supplier.shopName':
            '1688 shop of the last order. Pick one in the toolbar to work through a shop at a time.',
        'supplier.costCny':
            'Paid per store unit on the last 1688 order, parts of a set added up. Excludes shipping and order-level coupons, and is not today’s price.',
        'supplier.lastOrderDate': 'Date of the last 1688 order, China time.',
    };
    headerTooltipTarget: Element | null = null;
    headerTooltipText = '';
    desistedItemCount = 0;
    belowRopItemCount = 0;
    dataLoading = false;
    exporting = false;
    configPopupVisible = false;
    configSaving = false;
    // ---- Ordering mode (purchase-order tracking) ----
    orderingMode = false;
    orders: PurchaseOrder[] = []; // open + completed history, newest last
    // Derived from `orders` in applyOrdersDoc (open first, newest first) —
    // a field, not a getter, so template CD doesn't re-sort per cycle.
    ordersSorted: PurchaseOrder[] = [];
    private onOrderQtyByPid: Record<number, number> = {};
    // Items paused for ordering — hidden from the ordering-mode table until
    // restored — split into a short-term list ("skipping this round", restored
    // from its menu here) and a long-term one (its own page, GLP-180). The
    // split keeps a passing skip from burying the items that are parked
    // indefinitely. Both are persisted with the orders doc, and a pid sits in
    // at most one of them (re-pausing moves it).
    pausedTemp: PausedEntry[] = [];
    pausedLong: PausedEntry[] = [];
    pausedTempMenuItems: PausedMenuItem[] = [];
    // The popup's default height is "auto" with no cap, so a few dozen paused
    // items ran off the bottom of the screen with no way to reach them;
    // capped, the list inside scrolls.
    readonly pausedMenuDropDownOptions = { width: 460, maxHeight: '80vh' };
    pauseLongPopupVisible = false;
    pauseLongRow: any = null;
    pauseLongReason = '';
    // Union of both lists, rebuilt whenever the doc is applied, so
    // needsOrdering stays O(1) per row instead of scanning the lists.
    private activePausedPids = new Set<number>();
    ordersLoading = false;
    // A whole-document save from state that never loaded would erase the
    // stored orders, so persistDoc refuses until the first successful fetch.
    ordersLoaded = false;
    orderSaving = false;
    ordersPopupVisible = false;
    saveOrderPopupVisible = false;
    orderLabel = '';
    pendingLines: PurchaseOrderLine[] = [];
    // Rows with a typed order quantity — recounted at each mutation point
    // (cell commit, clear, pause, reload) rather than a getter, so the
    // header bindings don't scan the dataSource every CD cycle.
    typedLineCount = 0;
    // pid → row lookup rebuilt per data load; keeps lineSku/lineTitle O(1)
    // for the Orders popup and paused menu.
    private rowsByPid: Record<number, any> = {};
    // 1688 data, reloaded with the rows. Empty when its fetch failed, which
    // costs the links and nothing else on the page.
    supplierIndex: Supplier1688Index = EMPTY_SUPPLIER_INDEX;
    // Toolbar filter in ordering mode, to work through one shop at a time: a
    // shop URL, since two shops can go by one name. Null shows every shop;
    // NO_SUPPLIER_KEY shows products with no 1688 data.
    selectedSupplierShop: string | null = null;
    supplierShopItems: { value: string; text: string }[] = [];
    // Each order's lines by 1688 shop, keyed by order id. Derived from both
    // the orders doc and the 1688 data, whichever arrives last, as a field so
    // the popup's template doesn't regroup per CD cycle.
    orderSupplierViews: Record<string, OrderSupplierView> = {};
    // Manual ROP overrides by pid (persisted); effective row.rop = override
    // when present, else the lambda-calculated value (kept in rop_calculated).
    private ropOverrides: Record<number, number> = {};
    // Same protection as ordersLoaded: an override save spreads the local
    // map whole, so editing is blocked until the stored map has loaded, and
    // overlapping saves are collapsed into a trailing flush.
    private ropOverridesLoaded = false;
    private ropOverridesLoading = false;
    private ropOverrideSaving = false;
    private ropSaveQueued = false;
    // Row key (pid) set by onCellDblClick so onEditingStart lets that one
    // rop editor open — rop cells are editable only via double-click. Keyed
    // rather than a bare flag so a request that never reaches onEditingStart
    // cannot open some other row's editor on a later plain click.
    private ropEditRequestedFor: number | null = null;
    // Column views. The built-in ones show until the stored ones load; saving
    // is refused until then, for the same reason as ordersLoaded.
    readonly viewSelection = new ColumnViewSelection();
    columnViewsSaving = false;
    private columnViewsLoaded = false;
    // Tallest data row measured since the last window resize — kept rather
    // than retaken per render, see fitPageSizeToHeight.
    private tallestRowHeight = 0;
    private resizeTimer: ReturnType<typeof setTimeout> | null = null;
    // A filter pass that arrived while a cell editor was open, parked until
    // the editor closes — see refilterKeepingPage.
    private refilterPending = false;
    // Deferred callbacks (page restore, resize refit) must not touch the
    // grid once the page is gone.
    private destroyed = false;
    // The popup's settings as they stood when it opened. Non-null only while
    // an open popup has been neither applied nor saved, so a dismissal (X,
    // outside click, Esc) puts them back instead of leaving half-made edits
    // live for the next reload or "Save as Default".
    private configSnapshot: PopupSettings | null = null;
    // False when the saved settings could not be fetched: the page is then
    // running on the built-in defaults, and "Save as Default" would replace
    // the stored settings with them.
    private configLoaded = false;

    openConfig(): void {
        const snapshot = {} as PopupSettings;
        POPUP_SETTING_FIELDS.forEach((field) => {
            snapshot[field] = this[field];
        });
        this.configSnapshot = snapshot;
        this.configPopupVisible = true;
    }

    // Fires for every way the popup closes. Apply / Save drop the snapshot
    // first, so only a dismissal still has one to put back. A dismissal
    // mid-save is settled by the save's own outcome instead.
    onConfigPopupHidden(): void {
        if (!this.configSaving) {
            this.restoreConfigSnapshot();
        }
    }

    private restoreConfigSnapshot(): void {
        if (this.configSnapshot) {
            Object.assign(this, this.configSnapshot);
            this.configSnapshot = null;
        }
    }

    private refreshColumnViews(): void {
        this.columnViewsService
            .get()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (doc) => {
                    this.columnViewsLoaded = true;
                    this.viewSelection.use(doc);
                    this.applyActiveView();
                },
                error: (err: unknown) => this.notifyError(err),
            });
    }

    // Every data column of the grid, shown or not.
    private gridColumns(): { index: number; field: string; caption: string }[] {
        const grid = this.dataGrid.instance;
        return _range(grid.columnCount()).map((index) => ({
            index,
            field: grid.columnOption(index, 'dataField'),
            caption: grid.columnOption(index, 'caption'),
        }));
    }

    private applyActiveView(): void {
        const grid = this.dataGrid.instance;
        const view = this.viewSelection.active;
        grid.beginUpdate();
        this.gridColumns().forEach(({ index, field }) =>
            grid.columnOption(
                index,
                'visible',
                isColumnVisible(view, field, this.orderingMode),
            ),
        );
        grid.endUpdate();
    }

    onColumnViewPicked(e: { value?: string }): void {
        if (e.value && e.value !== this.viewSelection.activeId) {
            this.viewSelection.pick(e.value);
            this.applyActiveView();
        }
    }

    openColumnViews(): void {
        if (!this.columnViewsLoaded) {
            this.notifyError(
                'Column views have not loaded yet — retrying, try again shortly',
            );
            this.refreshColumnViews();
            return;
        }
        this.columnViewsPopup.open(
            this.viewSelection.doc,
            this.gridColumns().filter(
                ({ field }) => !ORDERING_ONLY_COLUMNS.includes(field),
            ),
        );
    }

    saveColumnViews(doc: ColumnViewsDoc): void {
        this.columnViewsSaving = true;
        this.columnViewsService
            .save(this.viewSelection.doc, doc)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (saved) => {
                    this.columnViewsSaving = false;
                    this.columnViewsPopup.close();
                    this.viewSelection.use(saved);
                    this.applyActiveView();
                    this.notificationService.pushMessage({
                        displayTime: 3000,
                        message: 'Column views saved',
                        messageType: MessageType.success,
                    });
                },
                error: (err: unknown) => {
                    this.columnViewsSaving = false;
                    this.notifyError(err);
                },
            });
    }

    openSupplierPage(): void {
        this.router.navigate(['/secure/utilities/reordering/1688']);
    }

    private openSupplierEditor(row: any): void {
        this.supplierEditor.open(
            { pid: row._id, sku: row.sku, title: row.title },
            this.supplierIndex,
        );
    }

    // Saved from the editor: the rows change in place, and the fetch that
    // would otherwise bring this about also recalculates the whole page.
    onSupplierOverridesSaved(doc: SupplierOverridesDoc): void {
        this.supplierIndex = buildSupplierIndex(this.supplierIndex.rows, doc);
        _each(this.dataSource, (row: any) => {
            row.supplier = this.supplierIndex.byPid[row._id] ?? null;
        });
        this.rebuildOrderSupplierViews();
        if (this.orderingMode) {
            this.refilterKeepingPage();
        } else {
            this.dataGrid.instance.refresh();
        }
    }

    toggleBelowRopFilter(): void {
        this.showOnlyBelowRop = !this.showOnlyBelowRop;
        this.onFilterSelectionChanged();
    }

    // ---------- Ordering mode (purchase-order tracking) ----------

    get openOrderCount(): number {
        return this.orders.filter((order) => order.status === 'open').length;
    }

    get pendingUnits(): number {
        return _sumBy(this.pendingLines, 'qty');
    }

    // The orders doc cannot take a write while either direction is in
    // flight: a save built during a fetch starts from the pre-fetch list,
    // and a fetch answered after a save may carry the pre-save document.
    get ordersBusy(): boolean {
        return this.orderSaving || this.ordersLoading;
    }

    orderUnits(order: PurchaseOrder): number {
        return _sumBy(order.lines, 'qty');
    }

    // Order lines store only the pid — resolve identity live from the loaded
    // inventory so titles are never stale; fall back to the pid for products
    // no longer loaded (hidden/delisted).
    lineSku(pid: number): string {
        return _get(this.rowByPid(pid), 'sku', `#${pid}`);
    }

    lineTitle(pid: number): string {
        return _get(this.rowByPid(pid), 'title', '(not in loaded inventory)');
    }

    private rowByPid(pid: number): any {
        return this.rowsByPid[pid];
    }

    private rebuildOrderSupplierViews(): void {
        const views: Record<string, OrderSupplierView> = {};
        this.orders.forEach((order) => {
            views[order.id] = buildOrderSupplierView(
                order,
                this.supplierIndex.byPid,
            );
        });
        this.orderSupplierViews = views;
    }

    // The header toggle switch. [value] is one-way bound to orderingMode, so
    // a cancelled exit just writes the widget value back and the guard below
    // swallows the echo event.
    onOrderingSwitchChanged(e: any): void {
        if (e.value === this.orderingMode) {
            return;
        }
        if (e.value) {
            this.orderingMode = true;
            this.viewSelection.enterOrdering();
            this.applyActiveView();
            // Resume a draft left by an earlier session before the first
            // filter pass, so its rows are counted and rendered in one go.
            this.applyOrderDraftToRows();
            this.recountTypedLines();
            this.onFilterSelectionChanged();
            // Re-fetch so the hidden-items rule reflects orders saved since
            // the last load, then re-apply the mode filter.
            this.refreshOrders(() => this.onFilterSelectionChanged());
            // needsOrdering reads the effective (overridden) rop, so a load
            // that failed at page init is retried here rather than letting
            // the mode hide rows on calculated ROPs alone.
            if (!this.ropOverridesLoaded) {
                this.refreshRopOverrides();
            }
            return;
        }
        const leave = () => {
            this.clearEnteredQuantities(false);
            this.orderingMode = false;
            this.selectedSupplierShop = null;
            this.viewSelection.leaveOrdering();
            this.applyActiveView();
            this.onFilterSelectionChanged();
        };
        if (this.typedLineCount === 0) {
            leave();
            return;
        }
        confirm(
            'Entered quantities will be discarded.',
            'Exit ordering mode?',
        ).then((ok) => {
            if (ok) {
                leave();
            } else {
                e.component.option('value', true);
            }
        });
    }

    // One definition of "this row has a typed order quantity": the rounded
    // integer the save flow would ship, so the button count, exit guard,
    // reload carry-over and saved lines can never disagree.
    private typedQty(row: any): number {
        const qty = Math.round(Number(row?.order_qty));
        return Number.isFinite(qty) && qty > 0 ? qty : 0;
    }

    private recountTypedLines(): void {
        let count = 0;
        _each(this.dataSource, (row: any) => {
            if (this.typedQty(row) > 0) {
                count++;
            }
        });
        this.typedLineCount = count;
        // Every mutation point routes through here, so this is the one place
        // the draft has to be written back.
        this.saveOrderDraft();
    }

    // Working state, kept in localStorage beside the grid layout so a reload
    // — or an accidental navigation away — resumes where the user left off
    // instead of discarding a part-typed order.
    //
    // Only ever written in ordering mode: view mode has no draft to record,
    // and writing from it would erase the one waiting to be restored. The
    // same goes for a page with no rows (first load failed or still
    // running) — "no typed rows" there says nothing about the draft.
    private saveOrderDraft(): void {
        if (!this.orderingMode || _isEmpty(this.dataSource)) {
            return;
        }
        // Loaded rows are authoritative for their own pid. Entries for
        // products missing from this fetch are carried forward untouched, so
        // a later reload that brings the row back can still restore it.
        const draft: Record<number, number> = {};
        _each(this.readOrderDraft(), (qty, pid) => {
            if (_isNil(this.rowsByPid[Number(pid)])) {
                draft[Number(pid)] = qty;
            }
        });
        _each(this.dataSource, (row: any) => {
            const qty = this.typedQty(row);
            if (qty > 0) {
                draft[row._id] = qty;
            }
        });
        if (_isEmpty(draft)) {
            this.clearOrderDraft();
            return;
        }
        try {
            localStorage.setItem(
                ORDER_DRAFT_STORAGE_KEY,
                JSON.stringify(draft),
            );
        } catch (err) {
            // Storage full or blocked — the draft just isn't resumable.
            console.error(err);
        }
    }

    private clearOrderDraft(): void {
        localStorage.removeItem(ORDER_DRAFT_STORAGE_KEY);
    }

    // The stored draft as { pid: qty }, keeping only usable quantities.
    private readOrderDraft(): Record<number, number> {
        let stored: unknown;
        try {
            stored = JSON.parse(
                localStorage.getItem(ORDER_DRAFT_STORAGE_KEY) ?? '{}',
            );
        } catch {
            return {};
        }
        const draft: Record<number, number> = {};
        if (_isNil(stored) || typeof stored !== 'object') {
            return draft;
        }
        _each(stored as Record<string, unknown>, (value, pid) => {
            const qty = Math.round(Number(value));
            if (Number.isFinite(qty) && qty > 0) {
                draft[Number(pid)] = qty;
            }
        });
        return draft;
    }

    // Put a stored draft back on the rows. Never overwrites a quantity
    // already on the row, so the in-memory carry-over across an in-mode
    // reload still wins over the older stored copy. Quantities on rows that
    // no longer need ordering are dropped by the filter pass that follows,
    // exactly as a freshly typed one would be.
    private applyOrderDraftToRows(): void {
        const draft = this.readOrderDraft();
        _each(this.dataSource, (row: any) => {
            const qty = draft[row._id];
            if (!_isNil(qty) && this.typedQty(row) === 0) {
                row.order_qty = qty;
            }
        });
    }

    // A cell commit landed (order_qty or rop) — keep the Save Order counter
    // in sync without scanning per CD cycle.
    onRowUpdated(): void {
        this.recountTypedLines();
        this.flushPendingRefilter();
    }

    // Fetch the purchase-orders doc and apply it. `after` (when given) is a
    // refilter, so the intermediate row refresh is skipped. In ordering mode
    // a filter pass follows every applied doc even without one: a plain row
    // refresh would hide the rows the doc newly covers or pauses but leave
    // their typed quantity behind to ship, unseen, with the next saved order.
    private refreshOrders(after?: () => void): void {
        // A fetch racing a save may be answered with the pre-save document
        // and revert what the save just applied; the save's own success path
        // already leaves local state current.
        if (this.orderSaving) {
            return;
        }
        this.ordersLoading = true;
        this.purchaseOrderService
            .getDoc()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (doc: PurchaseOrdersDoc) => {
                    this.ordersLoading = false;
                    this.ordersLoaded = true;
                    const refilter =
                        after ??
                        (this.orderingMode
                            ? () => this.refilterKeepingPage()
                            : undefined);
                    this.applyOrdersDoc(doc, !_isNil(refilter));
                    if (refilter) {
                        refilter();
                    }
                },
                error: (err: unknown) => {
                    this.ordersLoading = false;
                    this.notifyError(err);
                },
            });
    }

    // Single writer for everything derived from the orders doc — used by
    // both the load and the save path so they can't drift. When the caller
    // re-applies filters right after (willRefilter), the intermediate
    // full-grid refresh is skipped — but only in ordering mode, where the
    // fresh needsOrdering closure guarantees the filter pass re-renders. In
    // view mode the new filter expression can be deep-equal to the current
    // one (a DevExtreme no-op), which would leave the on-order markers
    // stale, so the rows are refreshed explicitly.
    private applyOrdersDoc(doc: PurchaseOrdersDoc, willRefilter = false): void {
        this.orders = doc.orders;
        this.pausedTemp = doc.paused_temp;
        this.pausedLong = doc.paused_long;
        // Open orders first, newest first within each group.
        this.ordersSorted = _orderBy(
            this.orders,
            [(order) => (order.status === 'open' ? 0 : 1), 'created_at'],
            ['asc', 'desc'],
        );
        this.onOrderQtyByPid = sumOpenOrderQty(this.orders);
        this.rebuildOrderSupplierViews();
        this.rebuildPausedState();
        this.applyOnOrderToRows(!(willRefilter && this.orderingMode));
    }

    // Every save replaces the whole document, so order-only mutations still
    // have to carry both pause lists forward untouched.
    private docWith(orders: PurchaseOrder[]): PurchaseOrdersDoc {
        return {
            orders,
            paused_temp: this.pausedTemp,
            paused_long: this.pausedLong,
        };
    }

    // In-place row mutation notifies neither the grid nor its function
    // filters — refresh() forces both to re-evaluate (skipped when the
    // caller re-applies filters right after, which re-renders anyway).
    private applyOnOrderToRows(refresh = true): void {
        _each(this.dataSource, (row: any) => {
            row.on_order_qty = this.onOrderQtyByPid[row._id] ?? 0;
        });
        if (refresh) {
            this.dataGrid.instance.refresh();
        }
    }

    // Mirror of isBelowRop on effective stock (on-hand + open-order qty):
    // items whose effective stock reaches the ROP are already covered and
    // hidden in ordering mode, as are items explicitly paused for ordering.
    // Deliberately ignores the typed order_qty so a row can never vanish
    // while the user is typing into it.
    private needsOrdering(item: any): boolean {
        if (this.activePausedPids.has(item._id)) {
            return false;
        }
        const effective = (item.remain ?? 0) + (item.on_order_qty ?? 0);
        return effective <= 0 || (!_isNil(item.rop) && effective <= item.rop);
    }

    // Right-click a row in ordering mode to hide the item from the ordering
    // table until it is restored — from the "Paused" menu or the long-term
    // page. Offered here rather than as a buttons column so the two icons
    // don't take fixed width from every row.
    // The grid defines no context menu of its own, so `items` may be absent.
    onContextMenuPreparing(e: {
        target?: string;
        row?: { rowType?: string; data?: any };
        items?: {
            text: string;
            icon?: string;
            disabled?: boolean;
            onItemClick: () => void;
        }[];
    }): void {
        if (
            e.target !== 'content' ||
            e.row?.rowType !== 'data' ||
            _isNil(e.row.data) ||
            !this.orderingMode
        ) {
            return;
        }
        const row = e.row.data;
        e.items = e.items || [];
        e.items.push(
            {
                text: 'Pause for now',
                icon: 'clock',
                disabled: this.ordersBusy,
                onItemClick: () => this.pauseRow(row, 'temp'),
            },
            {
                text: 'Pause long term',
                icon: 'lock',
                disabled: this.ordersBusy,
                onItemClick: () => this.openPauseLongPopup(row),
            },
            {
                text: 'Edit 1688 data',
                icon: 'link',
                onItemClick: () => this.openSupplierEditor(row),
            },
        );
    }

    openLongTermPage(): void {
        this.router.navigate(['/secure/utilities/reordering/long-term']);
    }

    private openPauseLongPopup(row: any): void {
        this.pauseLongRow = row;
        this.pauseLongReason = '';
        this.pauseLongPopupVisible = true;
    }

    // The popup stays open on a failed save, with the typed reason in it.
    confirmPauseLong(): void {
        this.pauseRow(this.pauseLongRow, 'long', this.pauseLongReason, () => {
            this.pauseLongPopupVisible = false;
        });
    }

    // Any typed quantity is discarded — only once the save confirms, so a
    // failed pause keeps the draft. While the document is busy, persistDoc
    // turns this down and says why; Enter in the popup gets here regardless of
    // the disabled button.
    private pauseRow(
        row: any,
        kind: PauseKind,
        reason?: string | null,
        afterSave?: () => void,
    ): void {
        if (_isNil(row)) {
            return;
        }
        const entry: PausedEntry = {
            pid: row._id,
            paused_at: moment().toISOString(),
        };
        // A cleared text box yields null.
        const trimmedReason = reason?.trim();
        if (trimmedReason) {
            entry.reason = trimmedReason;
        }
        // Drop the pid from BOTH lists first: re-pausing an already-paused
        // item moves it between horizons instead of leaving a duplicate
        // behind in the list it came from.
        const temporary = kind === 'temp';
        const paused_temp = this.withoutPid(this.pausedTemp, row._id);
        const paused_long = this.withoutPid(this.pausedLong, row._id);
        this.persistDoc(
            {
                orders: this.orders,
                paused_temp: temporary ? [...paused_temp, entry] : paused_temp,
                paused_long: temporary ? paused_long : [...paused_long, entry],
            },
            `Paused ${row.sku ?? row._id} ${
                temporary ? 'for now' : 'long term'
            }`,
            () => {
                delete row.order_qty;
                this.recountTypedLines();
                afterSave?.();
            },
        );
    }

    // "Restore all" is its own flagged entry, never inferred from a missing
    // pid — a click that carries no item, or a stored entry whose pid is
    // null, must not empty the list. It also asks first: it sits right under
    // the button and there is no undo short of re-pausing every row.
    onPausedTempMenuClick(e: any): void {
        const item: PausedMenuItem | null | undefined = e?.itemData;
        if (_isNil(item)) {
            return;
        }
        if (!item.restoreAll) {
            this.persistPausedTemp(
                this.withoutPid(this.pausedTemp, item.pid),
                'Item restored',
            );
            return;
        }
        confirm(
            `All ${this.pausedTemp.length} paused items go back into the ordering table.`,
            'Restore all?',
        ).then((ok) => {
            if (ok) {
                this.persistPausedTemp([], 'All paused items restored');
            }
        });
    }

    private persistPausedTemp(
        remaining: PausedEntry[],
        successMessage: string,
    ): void {
        this.persistDoc(
            {
                orders: this.orders,
                paused_temp: remaining,
                paused_long: this.pausedLong,
            },
            successMessage,
        );
    }

    // pid may be null only for a malformed stored entry — strict equality
    // still singles that one out.
    private withoutPid(
        entries: PausedEntry[],
        pid: number | null,
    ): PausedEntry[] {
        return entries.filter((entry) => entry.pid !== pid);
    }

    // Re-index both lists, relabel the menu and mark the rows. Called whenever
    // the doc is applied, and again once the rows load — the labels and marks
    // join the doc onto the loaded inventory, which the doc itself doesn't
    // carry. Both callers repaint the grid right after.
    private rebuildPausedState(): void {
        this.activePausedPids = new Set<number>([
            ...this.pausedTemp.map((entry) => entry.pid),
            ...this.pausedLong.map((entry) => entry.pid),
        ]);
        this.pausedTempMenuItems = this.buildPausedMenu(this.pausedTemp);
        const reasonByPid = new Map<number, string | undefined>(
            this.pausedLong.map((entry) => [entry.pid, entry.reason]),
        );
        _each(this.dataSource, (row: any) => {
            row.pause_reason = reasonByPid.get(row._id) ?? null;
        });
    }

    private buildPausedMenu(entries: PausedEntry[]): PausedMenuItem[] {
        const items: PausedMenuItem[] = _map(entries, (entry) => {
            // How long it has been sitting there — the cue for whether a
            // "skip this round" pause has quietly become permanent.
            // Absent on entries migrated from the pre-split list.
            const since = _isNil(entry.paused_at)
                ? ''
                : ` · paused ${moment(entry.paused_at).fromNow()}`;
            return {
                pid: entry.pid,
                text: `${this.lineSku(entry.pid)} · ${_truncate(
                    this.lineTitle(entry.pid),
                    { length: 40 },
                )}${since}`,
            };
        });
        if (!_isEmpty(items)) {
            items.unshift({ pid: null, text: 'Restore all', restoreAll: true });
        }
        return items;
    }

    // Order Qty is editable only in ordering mode; rop only via double-click
    // (any mode). Everything else stays read-only.
    onEditingStart(e: any): void {
        const field = e.column?.dataField;
        if (field === 'rop') {
            if (_isNil(e.key) || this.ropEditRequestedFor !== e.key) {
                e.cancel = true;
            }
            this.ropEditRequestedFor = null;
            return;
        }
        if (!this.orderingMode || field !== 'order_qty') {
            e.cancel = true;
        }
    }

    onCellDblClick(e: any): void {
        if (e.rowType === 'data' && e.column?.dataField === 'rop') {
            // An override save spreads the whole local map — editing before
            // the stored map has loaded would wipe the other overrides.
            if (!this.ropOverridesLoaded) {
                this.notificationService.pushMessage({
                    displayTime: 4000,
                    message: this.ropOverridesLoading
                        ? 'ROP overrides are still loading — try again in a moment'
                        : 'ROP overrides failed to load — retrying, try again in a moment',
                    messageType: MessageType.error,
                });
                this.refreshRopOverrides();
                return;
            }
            // A double-click inside an editor that is already open (selecting
            // the number): editCell is a no-op there and never reaches
            // onEditingStart, so the request would stay armed.
            if (e.cellElement?.classList?.contains('dx-editor-cell')) {
                return;
            }
            this.ropEditRequestedFor = e.key;
            this.dataGrid.instance.editCell(e.rowIndex, 'rop');
        }
    }

    // A rop cell edit landed (cell mode): a number becomes a persisted
    // override; clearing the cell removes the override and restores the
    // lambda-calculated value.
    onRowUpdating(e: any): void {
        const newData = e.newData ?? {};
        if (!('rop' in newData)) {
            return; // order_qty edits pass through untouched
        }
        const pid = e.oldData?._id;
        if (_isNil(newData.rop)) {
            newData.rop = e.oldData?.rop_calculated ?? 0;
            newData.rop_overridden = false;
            this.persistRopOverride(pid, null);
        } else {
            newData.rop_overridden = true;
            this.persistRopOverride(pid, Number(newData.rop));
        }
    }

    private persistRopOverride(pid: number, value: number | null): void {
        // Commit locally first, so a second edit made during the save
        // round-trip builds on this one instead of silently dropping it.
        if (_isNil(value)) {
            delete this.ropOverrides[pid];
        } else {
            this.ropOverrides[pid] = value;
        }
        this.queueRopOverridesSave(
            _isNil(value) ? 'ROP override removed' : 'ROP override saved',
        );
    }

    // Whole-map saves are serialized: while one is in flight, further edits
    // just mark a trailing flush of the (already updated) local map. Neither
    // is cancelled with the page: an edit already made must still reach the
    // store, however the user leaves.
    private queueRopOverridesSave(message: string): void {
        if (this.ropOverrideSaving) {
            this.ropSaveQueued = true;
            return;
        }
        this.ropOverrideSaving = true;
        this.ropOverrideService
            .saveOverrides({ ...this.ropOverrides })
            .subscribe({
                next: () => {
                    this.ropOverrideSaving = false;
                    if (this.ropSaveQueued) {
                        this.ropSaveQueued = false;
                        this.queueRopOverridesSave(message);
                        return;
                    }
                    // Effective ROP changed: re-apply filters only when one
                    // of them actually reads rop; otherwise the committed
                    // cell repaint suffices and only the count needs work.
                    if (!this.destroyed) {
                        if (this.showOnlyBelowRop || this.orderingMode) {
                            this.refilterKeepingPage();
                        } else {
                            this.countBelowRopItem();
                        }
                    }
                    this.notificationService.pushMessage({
                        displayTime: 3000,
                        message,
                        messageType: MessageType.success,
                    });
                },
                error: (err: unknown) => {
                    this.ropOverrideSaving = false;
                    this.ropSaveQueued = false;
                    this.notifyError(err);
                    // Restore the stored truth (also rolls the rows back).
                    if (!this.destroyed) {
                        this.refreshRopOverrides();
                    }
                },
            });
    }

    // Also the retry: a failed load leaves ropOverridesLoaded false, and the
    // places that need the map (a rop double-click, entering ordering mode)
    // call this again instead of waiting on a load that will never come.
    private refreshRopOverrides(): void {
        if (this.ropOverridesLoading) {
            return;
        }
        this.ropOverridesLoading = true;
        this.ropOverrideService
            .getOverrides()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (overrides) => {
                    this.ropOverridesLoading = false;
                    this.ropOverrides = overrides;
                    this.ropOverridesLoaded = true;
                    this.applyRopOverridesToRows();
                },
                error: (err: unknown) => {
                    this.ropOverridesLoading = false;
                    this.notifyError(err);
                },
            });
    }

    // Re-materialize effective rop onto the loaded rows (mirror of what
    // prepareReorderingData does when the overrides are already known).
    private applyRopOverridesToRows(): void {
        _each(this.dataSource, (row: any) => {
            const override = this.ropOverrides[row._id];
            const overridden = !_isNil(override);
            row.rop = overridden ? override : (row.rop_calculated ?? row.rop);
            row.rop_overridden = overridden;
        });
        // A filter that reads rop needs a full pass (in ordering mode that is
        // also what drops the draft of a row the new rop now covers);
        // otherwise repainting the rows is enough.
        if (this.showOnlyBelowRop || this.orderingMode) {
            this.refilterKeepingPage();
        } else {
            this.countBelowRopItem();
            this.dataGrid.instance.refresh();
        }
    }

    // Projected stock if this order line is placed (arrow property so the
    // grid can call it without a bound this).
    calculateProjectedTotal = (row: any): number =>
        (row.remain ?? 0) +
        (row.on_order_qty ?? 0) +
        (Number(row.order_qty) || 0);

    // refresh=false when the caller re-applies filters right after (that
    // pass re-renders the grid anyway).
    private clearEnteredQuantities(refresh = true): void {
        _each(this.dataSource, (row: any) => {
            delete row.order_qty;
        });
        this.typedLineCount = 0;
        // Both callers are a deliberate discard — the order was saved, or the
        // user confirmed leaving ordering mode — so the stored draft goes too,
        // otherwise it would resurface the next time the mode is switched on.
        this.clearOrderDraft();
        if (refresh) {
            this.dataGrid.instance.refresh();
        }
    }

    // Commit any in-flight cell editor, collect the typed quantities and
    // open the label/confirm popup. Collects from the whole dataSource, in
    // line with the button count — rows hidden by the class filter still
    // ship; rows that stopped needing ordering had their draft discarded by
    // onFilterSelectionChanged.
    openSaveOrderPopup(): void {
        Promise.resolve(this.dataGrid.instance.saveEditData()).then(() => {
            // A filter pass parked behind the editor may not have run yet;
            // what it guards — covered rows shipping unseen — is settled
            // here regardless.
            this.dropCoveredDrafts();
            const lines: PurchaseOrderLine[] = [];
            _each(this.dataSource, (row: any) => {
                const qty = this.typedQty(row);
                if (qty > 0) {
                    lines.push({ pid: row._id, qty });
                }
            });
            if (_isEmpty(lines)) {
                this.notificationService.pushMessage({
                    displayTime: 4000,
                    message: 'Enter at least one order quantity first',
                    messageType: MessageType.error,
                });
                return;
            }
            this.pendingLines = lines;
            this.orderLabel = '';
            this.saveOrderPopupVisible = true;
        });
    }

    confirmSaveOrder(): void {
        // The popup's text box submits via Enter, bypassing the disabled
        // Save button — don't let a second Enter re-enter mid-save.
        if (this.orderSaving) {
            return;
        }
        const order: PurchaseOrder = {
            id: `PO-${moment().format('YYMMDD-HHmmss')}`,
            created_at: moment().toISOString(),
            status: 'open',
            lines: this.pendingLines,
        };
        const label = this.orderLabel.trim();
        if (label) {
            order.label = label;
        }
        this.persistDoc(
            this.docWith([...this.orders, order]),
            'Purchase order saved',
            () => {
                this.saveOrderPopupVisible = false;
                this.clearEnteredQuantities(false);
            },
        );
    }

    // Replace the whole stored document; local state is only updated after
    // the lambda confirms, so a failed save leaves everything untouched
    // (onError lets callers roll their own widgets back). Refuses to write
    // from never-loaded state — that would erase the stored orders — and
    // while another save is in flight, since both docs would be built from
    // the same stale base.
    private persistDoc(
        next: PurchaseOrdersDoc,
        successMessage: string,
        after?: () => void,
        onError?: () => void,
    ): void {
        if (!this.ordersLoaded || this.ordersBusy) {
            let reason =
                'Purchase orders have not loaded yet — retrying the load, try again shortly';
            if (this.orderSaving) {
                reason = 'Another save is still in progress — try again';
            } else if (this.ordersLoading) {
                // `next` was built from the list as it stood before this
                // fetch; whatever the fetch brings would be overwritten.
                reason =
                    'Purchase orders are still loading — try again in a moment';
            } else {
                this.refreshOrders();
            }
            this.notifyError(reason);
            if (onError) {
                onError();
            }
            return;
        }
        this.orderSaving = true;
        this.purchaseOrderService
            .saveDoc(next)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.orderSaving = false;
                    this.applyOrdersDoc(next, true);
                    if (after) {
                        after();
                    }
                    // Coverage/paused state changed — one filter pass keeps
                    // every caller consistent (harmless in view mode). None
                    // of these writes is a filter change from the user's
                    // point of view: pausing row 3 of page 7 should leave
                    // them on page 7.
                    this.refilterKeepingPage();
                    this.notificationService.pushMessage({
                        displayTime: 3000,
                        message: successMessage,
                        messageType: MessageType.success,
                    });
                },
                error: (err: unknown) => {
                    this.orderSaving = false;
                    this.notifyError(err);
                    if (onError) {
                        onError();
                    }
                },
            });
    }

    // One filter pass that leaves the user where they were. filter() resets
    // paging to the first page and reloads, and in cell mode a reload that
    // changes the page closes any open editor. The callers are all
    // background triggers (a save answering, a doc refresh), so none of them
    // may run it mid-typing: the pass is parked until the editor closes.
    private refilterKeepingPage(): void {
        if (this.isCellEditorOpen()) {
            this.refilterPending = true;
            return;
        }
        const page = this.currentPageIndex();
        this.onFilterSelectionChanged();
        this.restorePageIndex(page);
    }

    // Called wherever an editor may just have closed: a committed edit
    // (onRowUpdated) and any re-render (onGridContentReady).
    private flushPendingRefilter(): void {
        if (this.refilterPending && !this.isCellEditorOpen()) {
            this.refilterKeepingPage();
        }
    }

    // Scoped to the rows view: the filter row's cells carry the same
    // dx-editor-cell class permanently and must not match.
    private isCellEditorOpen(): boolean {
        return !!this.dataGrid?.instance
            ?.element()
            ?.querySelector('.dx-datagrid-rowsview td.dx-editor-cell');
    }

    private currentPageIndex(): number {
        try {
            return this.dataGrid?.instance?.pageIndex() ?? 0;
        } catch {
            return 0;
        }
    }

    // Deferred: filter() reloads the grid asynchronously and settles paging
    // as part of that pass, so setting the index inline would be overwritten.
    // Clamped because the write may well have removed rows — pausing the last
    // item on the last page shortens the list.
    private restorePageIndex(page: number): void {
        if (page <= 0) {
            return;
        }
        setTimeout(() => {
            const grid = this.dataGrid?.instance;
            if (!grid || this.destroyed) {
                return;
            }
            const last = Math.max(0, grid.pageCount() - 1);
            grid.pageIndex(Math.min(page, last));
        });
    }

    private notifyError(err: unknown): void {
        console.error(err);
        this.notificationService.pushMessage({
            displayTime: 6000,
            // errorToString JSON-encodes a bare string, quotes and all.
            message:
                typeof err === 'string'
                    ? err
                    : this.utilService.errorToString(err),
            messageType: MessageType.error,
        });
    }

    openOrdersPopup(): void {
        this.refreshOrders();
        this.ordersPopupVisible = true;
    }

    // Inline qty edit on an open order's line. Invalid input reverts the
    // editor; a valid change persists immediately.
    onLineQtyChanged(
        order: PurchaseOrder,
        line: PurchaseOrderLine,
        e: any,
    ): void {
        const qty = Math.round(Number(e.value));
        if (!Number.isFinite(qty) || qty <= 0) {
            e.component.option('value', line.qty);
            return;
        }
        if (qty === line.qty) {
            return;
        }
        const next = _map(this.orders, (o) =>
            o.id === order.id
                ? {
                      ...o,
                      lines: _map(o.lines, (l) =>
                          l.pid === line.pid ? { ...l, qty } : l,
                      ),
                  }
                : o,
        );
        this.persistDoc(
            this.docWith(next),
            'Order updated',
            undefined,
            // Failed save: put the widget back on the stored qty so the
            // display can't diverge from the order.
            () => e.component.option('value', line.qty),
        );
    }

    removeOrderLine(order: PurchaseOrder, line: PurchaseOrderLine): void {
        const remaining = order.lines.filter((l) => l.pid !== line.pid);
        if (_isEmpty(remaining)) {
            confirm(
                `Removing the last item deletes ${order.id}.`,
                'Delete order?',
            ).then((ok) => {
                if (!ok) {
                    return;
                }
                this.persistDoc(
                    this.docWith(this.orders.filter((o) => o.id !== order.id)),
                    'Order deleted',
                );
            });
            return;
        }
        const next = _map(this.orders, (o) =>
            o.id === order.id ? { ...o, lines: remaining } : o,
        );
        this.persistDoc(this.docWith(next), 'Item removed from order');
    }

    // Removes the order entirely (open or completed). Deleting an open
    // order stops it counting toward on-order stock, so covered rows
    // reappear via persistDoc's refilter.
    deleteOrder(order: PurchaseOrder): void {
        confirm(
            `${order.id} will be removed permanently.`,
            'Delete order?',
        ).then((ok) => {
            if (!ok) {
                return;
            }
            this.persistDoc(
                this.docWith(this.orders.filter((o) => o.id !== order.id)),
                'Order deleted',
            );
        });
    }

    completeOrder(order: PurchaseOrder): void {
        confirm(`Mark ${order.id} as received?`, 'Complete order?').then(
            (ok) => {
                if (!ok) {
                    return;
                }
                const next = _map(this.orders, (o) =>
                    o.id === order.id
                        ? {
                              ...o,
                              status: 'completed' as const,
                              completed_at: moment().toISOString(),
                          }
                        : o,
                );
                this.persistDoc(this.docWith(next), 'Order completed');
            },
        );
    }

    reopenOrder(order: PurchaseOrder): void {
        const next = _map(this.orders, (o) => {
            if (o.id !== order.id) {
                return o;
            }
            const reopened: PurchaseOrder = { ...o, status: 'open' };
            delete reopened.completed_at;
            return reopened;
        });
        this.persistDoc(this.docWith(next), 'Order reopened');
    }

    classCount(abcClass: string): string {
        const stat = this.classStats[abcClass];
        return _isNil(stat) ? '0' : `${stat.count}`;
    }

    // Share of loaded items (not revenue) in this class.
    classItemPct(abcClass: string): string {
        const stat = this.classStats[abcClass];
        return _isNil(stat) ? '0%' : `${stat.itemPct.toFixed(1)}%`;
    }

    // Show a description/formula tooltip while hovering the header cell of a
    // calculated column.
    onCellHoverChanged(e: any): void {
        if (
            e.rowType === 'header' &&
            e.eventType === 'mouseover' &&
            this.columnDescriptions[e.column?.dataField]
        ) {
            this.headerTooltipText =
                this.columnDescriptions[e.column.dataField];
            this.headerTooltipTarget = e.cellElement;
        } else if (e.eventType === 'mouseout') {
            this.headerTooltipTarget = null;
        }
    }

    onGridContentReady(): void {
        this.flushPendingRefilter();
        this.fitPageSizeToHeight();
    }

    // Debounced: a dragged window edge or a zoom gesture fires many events a
    // second, and every refit that crosses a row boundary reloads the grid.
    @HostListener('window:resize')
    onWindowResize(): void {
        if (this.resizeTimer !== null) {
            clearTimeout(this.resizeTimer);
        }
        this.resizeTimer = setTimeout(() => {
            this.resizeTimer = null;
            if (this.destroyed) {
                return;
            }
            // Browser zoom and a move between monitors both arrive as a
            // resize and both change the row height, so the remembered one
            // is only good until the next resize.
            this.tallestRowHeight = 0;
            this.fitPageSizeToHeight();
        }, RESIZE_DEBOUNCE_MS);
    }

    // Size each page to exactly the rows that fit the grid's fixed height,
    // so the data area never scrolls vertically. Re-runs on every render
    // (contentReady) and window resize.
    //
    // A pageSize change reloads the grid and fires contentReady again, so the
    // result must not depend on which page is rendered. Measured fresh it
    // did: rows differ by a fraction of a pixel, and when the height lands
    // within that fraction of a whole row (610 / 34 = 17.94) a 15-row page
    // answered 17 and the 1-row last page answered 18 — each reload handed
    // over to the other and the grid flickered between "4 of 4" and "5 of 5"
    // until the window was resized. Folding every measurement into the
    // tallest seen means that for a given grid height the fit can only
    // shrink, so it settles.
    private fitPageSizeToHeight(): void {
        const grid = this.dataGrid.instance;
        const gridElement = grid.element();
        // Don't refit while a cell editor is open — a pageSize change
        // repaints the grid and would destroy the editor mid-typing.
        if (this.isCellEditorOpen()) {
            return;
        }
        const rowsView = gridElement.querySelector('.dx-datagrid-rowsview');
        const dataRows = rowsView?.querySelectorAll('.dx-data-row') ?? [];
        if (!rowsView || dataRows.length === 0) {
            return;
        }
        // getBoundingClientRect includes the 1px row-line borders that
        // clientHeight excludes — those add up across a page and used to
        // overfill it; the 4px slack absorbs sub-pixel rounding.
        //
        // Measured across every rendered row, not just the first: a short row
        // at the top would otherwise set the budget for taller ones below and
        // overfill the page. The stylesheet pins the line box so they should
        // all match — this is what keeps a future style that breaks that
        // from silently bringing the scrollbar back.
        let rowHeight = this.tallestRowHeight;
        dataRows.forEach((row: Element) => {
            rowHeight = Math.max(rowHeight, row.getBoundingClientRect().height);
        });
        if (rowHeight === 0) {
            return;
        }
        this.tallestRowHeight = rowHeight;
        const fit = Math.max(
            1,
            Math.floor((rowsView.clientHeight - 4) / rowHeight),
        );
        const pageSize = grid.pageSize();
        if (fit !== pageSize) {
            // pageSize() goes back to the first page; return to the page
            // that holds the row the user was looking at.
            const firstRow = grid.pageIndex() * pageSize;
            grid.pageSize(fit);
            this.restorePageIndex(Math.floor(firstRow / fit));
        }
    }

    // Persist only the column layout (order/width/sort) — not filters or
    // paging, which this component drives programmatically on every load.
    // Visibility is the column view's: the grid ignores `visible` in a restored
    // state while its column chooser is off, which it is here.
    saveGridState = (state: any): void => {
        const columns = Array.isArray(state?.columns)
            ? this.stripOrderingModeColumns(state.columns)
            : null;
        try {
            localStorage.setItem(
                GRID_STATE_STORAGE_KEY,
                JSON.stringify(columns ? { columns } : {}),
            );
        } catch (err) {
            // Storage full or blocked — the layout just isn't remembered.
            console.error(err);
        }
    };

    loadGridState = (): object => {
        try {
            const state = JSON.parse(
                localStorage.getItem(GRID_STATE_STORAGE_KEY) ?? '{}',
            );
            if (Array.isArray(state?.columns)) {
                state.columns = this.stripOrderingModeColumns(state.columns);
            }
            return state;
        } catch {
            return {};
        }
    };

    private stripOrderingModeColumns(columns: any[]): any[] {
        return columns.filter(
            (column: any) =>
                !_includes(
                    ORDERING_MODE_COLUMNS,
                    column?.dataField ?? column?.name,
                ),
        );
    }

    // Apply the current settings for this session only (no persistence).
    applyConfig(): void {
        this.sanitizeSettings();
        if (!this.validateServiceLevelOrder()) {
            return; // keep the popup open for correction
        }
        this.configSnapshot = null; // accepted — nothing to put back
        this.configPopupVisible = false;
        this.loadReorderingData();
    }

    // The tiers must not invert — a lower class must never carry a higher
    // service level (and thus more safety stock) than a higher class.
    private validateServiceLevelOrder(): boolean {
        if (
            this.serviceLevelA >= this.serviceLevelB &&
            this.serviceLevelB >= this.serviceLevelOther
        ) {
            return true;
        }
        this.notificationService.pushMessage({
            displayTime: 6000,
            message:
                'Service levels must be ordered: Class A ≥ Class B ≥ Other',
            messageType: MessageType.error,
        });
        return false;
    }

    // Persist the current settings via the App Config Store lambda, then reload.
    saveConfig(): void {
        this.sanitizeSettings();
        if (!this.validateServiceLevelOrder()) {
            return; // keep the popup open for correction
        }
        if (this.configLoaded) {
            this.writeConfig();
            return;
        }
        // The values on screen started from the built-in defaults, not from
        // what is stored — make overwriting the stored settings a decision.
        confirm(
            'The saved settings could not be loaded earlier, so the values shown started from the built-in defaults. Saving replaces the stored settings with them.',
            'Replace saved settings?',
        ).then((ok) => {
            if (ok) {
                this.writeConfig();
            }
        });
    }

    private writeConfig(): void {
        const config: ReorderingConfig = {
            calculationDays: this.calculationDays,
            orderingLeadTimeDays: this.reorderingLeadTimeDays,
            demandForDays: this.demandForDays,
            serviceLevelA: this.serviceLevelA,
            serviceLevelB: this.serviceLevelB,
            serviceLevelOther: this.serviceLevelOther,
            orderingCost: this.orderingCost,
            holdingRate: this.holdingRate,
            costRatio: this.costRatio,
        };
        this.configSaving = true;
        this.reorderingService
            .saveConfig(config)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.configSaving = false;
                    this.configLoaded = true; // stored now equals shown
                    this.configSnapshot = null;
                    this.configPopupVisible = false;
                    this.notificationService.pushMessage({
                        displayTime: 3000,
                        message: 'Reorder settings saved',
                        messageType: MessageType.success,
                    });
                    this.loadReorderingData();
                },
                error: (err) => {
                    this.configSaving = false;
                    this.notifyError(err);
                    // Dismissed mid-save: nothing was stored, so the edits
                    // go back as they would have on any other dismissal.
                    if (!this.configPopupVisible) {
                        this.restoreConfigSnapshot();
                    }
                },
            });
    }

    ngAfterViewInit(): void {
        this.headerContentService.set(this.headerMenu);
        this.loadConfig();
        // In parallel with the data load, so "Orders (n)" is real from the
        // start and view mode can mark rows that are already on order.
        this.refreshOrders();
        this.refreshRopOverrides();
        this.applyActiveView();
        this.refreshColumnViews();
        this.cdref.detectChanges();
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        if (this.resizeTimer !== null) {
            clearTimeout(this.resizeTimer);
        }
        this.headerContentService.clear(this.headerMenu);
    }

    // Restore saved settings (if any) before the first data load; falls back to
    // the built-in defaults when nothing has been saved or the fetch fails —
    // and says so in the latter case, since every figure on the page then
    // comes from settings the user did not choose.
    private loadConfig(): void {
        this.dataLoading = true;
        this.dataGrid.instance.beginCustomLoading('');
        this.reorderingService
            .getConfig()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (config) => {
                    this.configLoaded = true;
                    this.applyConfigValues(config);
                    this.dataGrid.instance.endCustomLoading();
                    this.dataLoading = false;
                    this.loadReorderingData();
                },
                error: (err) => {
                    console.error(err);
                    this.notificationService.pushMessage({
                        displayTime: 10000,
                        message: `Saved reorder settings could not be loaded — calculating with the built-in defaults. ${this.utilService.errorToString(
                            err,
                        )}`,
                        messageType: MessageType.error,
                    });
                    this.dataGrid.instance.endCustomLoading();
                    this.dataLoading = false;
                    this.loadReorderingData();
                },
            });
    }

    // Accept a persisted numeric setting only when it lies in the given range
    // (mirroring the UI input bounds) — the config lambda stores documents
    // verbatim, so an out-of-band edit can hold values the analytics lambdas
    // reject (serviceLevel outside (0,1), calculationDays < 30, …), which
    // would otherwise fail every load.
    private applyIfInRange(
        value: number | null | undefined,
        min: number,
        max: number,
        assign: (value: number) => void,
    ): void {
        if (!_isNil(value) && value >= min && value <= max) {
            assign(value);
        }
    }

    private applyConfigValues(config: ReorderingConfig | null): void {
        if (_isNil(config)) {
            return;
        }
        this.applyIfInRange(
            config.calculationDays,
            ...SETTING_BOUNDS.calculationDays,
            (v) => {
                this.calculationDays = v;
            },
        );
        this.applyIfInRange(
            config.orderingLeadTimeDays,
            ...SETTING_BOUNDS.reorderingLeadTimeDays,
            (v) => {
                this.reorderingLeadTimeDays = v;
            },
        );
        this.applyIfInRange(
            config.demandForDays,
            ...SETTING_BOUNDS.demandForDays,
            (v) => {
                this.demandForDays = v;
            },
        );
        // Configs saved before the per-class split carry a single legacy
        // serviceLevel — apply it to every class the config doesn't override.
        this.applyIfInRange(
            config.serviceLevelA ?? config.serviceLevel,
            SERVICE_LEVEL_MIN,
            SERVICE_LEVEL_MAX,
            (v) => {
                this.serviceLevelA = v;
            },
        );
        this.applyIfInRange(
            config.serviceLevelB ?? config.serviceLevel,
            SERVICE_LEVEL_MIN,
            SERVICE_LEVEL_MAX,
            (v) => {
                this.serviceLevelB = v;
            },
        );
        this.applyIfInRange(
            config.serviceLevelOther ?? config.serviceLevel,
            SERVICE_LEVEL_MIN,
            SERVICE_LEVEL_MAX,
            (v) => {
                this.serviceLevelOther = v;
            },
        );
        this.applyIfInRange(
            config.orderingCost,
            ...SETTING_BOUNDS.orderingCost,
            (v) => {
                this.orderingCost = v;
            },
        );
        this.applyIfInRange(
            config.holdingRate,
            ...SETTING_BOUNDS.holdingRate,
            (v) => {
                this.holdingRate = v;
            },
        );
        this.applyIfInRange(
            config.costRatio,
            ...SETTING_BOUNDS.costRatio,
            (v) => {
                this.costRatio = v;
            },
        );
    }

    // Every setting, before any load or save. A cleared number box yields
    // null: for a service level fetchRop would silently omit it from the
    // payload (the lambda then defaults to 0.5 = no safety stock), and the
    // others are worse — see DEFAULT_SETTINGS. Heal cleared or out-of-range
    // values back to the built-in defaults so the UI always shows the value
    // that is actually applied and saved.
    private sanitizeSettings(): void {
        this.sanitizeServiceLevels();
        (
            Object.keys(DEFAULT_SETTINGS) as (keyof typeof DEFAULT_SETTINGS)[]
        ).forEach((field) => {
            const [min, max] = SETTING_BOUNDS[field];
            const value: number | null | undefined = this[field];
            if (_isNil(value) || !(value >= min && value <= max)) {
                this[field] = DEFAULT_SETTINGS[field];
            }
        });
    }

    private sanitizeServiceLevels(): void {
        this.serviceLevelA = this.sanitizeServiceLevel(
            this.serviceLevelA,
            DEFAULT_SERVICE_LEVELS.A,
        );
        this.serviceLevelB = this.sanitizeServiceLevel(
            this.serviceLevelB,
            DEFAULT_SERVICE_LEVELS.B,
        );
        this.serviceLevelOther = this.sanitizeServiceLevel(
            this.serviceLevelOther,
            DEFAULT_SERVICE_LEVELS.other,
        );
    }

    private sanitizeServiceLevel(
        value: number | null | undefined,
        fallback: number,
    ): number {
        return !_isNil(value) &&
            value >= SERVICE_LEVEL_MIN &&
            value <= SERVICE_LEVEL_MAX
            ? value
            : fallback;
    }

    loadReorderingData() {
        if (!this.dataLoading) {
            this.sanitizeSettings();
            // Every path into a calculation goes through here, so this is
            // where inverted tiers are stopped — not only the popup buttons.
            if (!this.validateServiceLevelOrder()) {
                return;
            }
            this.dataLoading = true;
            this.dataGrid.instance.beginCustomLoading('');
            this.fetchReorderingData()
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(
                    (result) => {
                        this.prepareReorderingData(result);
                        // Carry typed order quantities across an in-mode
                        // reload (Update button / settings change while
                        // ordering).
                        if (this.orderingMode) {
                            const carried: Record<number, number> = {};
                            _each(this.dataSource, (row: any) => {
                                const qty = this.typedQty(row);
                                if (qty > 0) {
                                    carried[row._id] = qty;
                                }
                            });
                            _each(result, (row: any) => {
                                if (!_isNil(carried[row._id])) {
                                    row.order_qty = carried[row._id];
                                }
                            });
                        }
                        this.dataSource = result;
                        this.rowsByPid = _keyBy(result, '_id');
                        // Rows the carry-over above didn't cover (a reload
                        // that brought back a row dropped from the previous
                        // fetch) still get their stored quantity back.
                        if (this.orderingMode) {
                            this.applyOrderDraftToRows();
                        }
                        this.recountTypedLines();
                        // Paused-menu labels resolve sku/title from the rows.
                        this.rebuildPausedState();
                        this.rebuildOrderSupplierViews();
                        // A new array in [dataSource] discards the grid's
                        // filter. Hand the rows over now rather than on the
                        // next CD cycle, so the filter goes back on in the
                        // same turn — the grid never paints the unfiltered
                        // list (in ordering mode: every covered and paused
                        // product), and nothing is left on a timer.
                        this.cdref.detectChanges();
                        this.onFilterSelectionChanged();

                        this.dataGrid.instance.endCustomLoading();
                        this.dataLoading = false;
                    },
                    (err) => {
                        this.dataGrid.instance.endCustomLoading();
                        this.notifyError(err);
                        this.dataLoading = false;
                    },
                );
        }
    }

    private fetchReorderingData() {
        const inventoryObservable = this.unifiedQueryV2Service.query({
            db: 'lnw_crawler',
            col: 'inventory',
            query: ORDERABLE_PRODUCTS_QUERY,
            options: {
                projection: {
                    sku: 1,
                    title: 1,
                    price: 1,
                    is_show: 1,
                    remain: 1,
                    stock_status: 1,
                    crawler_last_updated: 1,
                    product_type: 1,
                },
            },
        });
        // One ROP call per distinct class service level (A/B/Other may share
        // a level) — the lambda owns the safety-stock math; each product then
        // takes the rop from its class's response.
        const ropServiceLevels = _uniq([
            this.serviceLevelA,
            this.serviceLevelB,
            this.serviceLevelOther,
        ]);
        const ropObservable = forkJoin(
            ropServiceLevels.map((serviceLevel) =>
                this.reorderingService.fetchRop(
                    {},
                    this.calculationDays,
                    this.reorderingLeadTimeDays,
                    serviceLevel,
                ),
            ),
        );
        const eoqObservable = this.reorderingService.fetchEoq(
            {},
            this.calculationDays,
            this.orderingCost,
            this.holdingRate,
            this.costRatio,
        );
        const abcObservable: Observable<AbcAnalysisResponse[]> =
            this.abcAnalysisService.get(moment().subtract(1, 'year'));
        // Average on-hand stock per product over the calc window, from the
        // crawler's daily inventory_snapshots (one row per product per
        // Bangkok-date; history begins 2026-08-08, so until a full window has
        // accumulated the average covers only the recorded days). date is a
        // "YYYY-MM-DD" string, so $gte compares correctly lexicographically.
        const avgStockObservable = this.unifiedQueryV2Service.aggregate({
            db: 'lnw_crawler',
            col: 'inventory_snapshots',
            pipeline: [
                {
                    $match: {
                        date: {
                            $gte: moment()
                                .subtract(this.calculationDays, 'days')
                                .format('YYYY-MM-DD'),
                        },
                    },
                },
                {
                    $group: {
                        _id: '$pid',
                        avg_stock: { $avg: '$remain' },
                        snapshot_days: { $sum: 1 },
                    },
                },
            ],
        });
        const supplierObservable = this.supplier1688Service
            .getRows()
            .pipe(
                this.reportAndUse(
                    '1688 links could not be loaded',
                    [] as Supplier1688Row[],
                ),
            );
        // Going on without these shows matched values someone has corrected,
        // so it is said out loud.
        const supplierOverridesObservable = this.supplier1688Service
            .getOverrides()
            .pipe(
                this.reportAndUse(
                    '1688 values set by hand could not be loaded, showing matched values only',
                    EMPTY_SUPPLIER_OVERRIDES,
                ),
            );

        return forkJoin([
            inventoryObservable,
            ropObservable,
            eoqObservable,
            abcObservable,
            avgStockObservable,
            supplierObservable,
            supplierOverridesObservable,
        ]).pipe(
            map((responses: any[]) => {
                const inventoryData = responses[0];
                const ropResponses: RopResponse[] = responses[1];
                const eoqData: EoqRow[] = responses[2].eoq_result;
                const abcAnalysisData: AbcAnalysisResponse[] = responses[3];
                const avgStockData: any[] = responses[4];
                this.supplierIndex = buildSupplierIndex(
                    responses[5],
                    responses[6],
                );

                // Keep identity fields out of the analytics picks (inventory
                // is the source of truth for sku/title) and drop eoq's
                // unit_sold/data_days/note, which would clobber the rop
                // copies in the merge; every other metric is kept so the
                // column views can surface it.
                const abcSlim = _map(abcAnalysisData, (row) =>
                    _pick(row, ['pid', 'class', 'total_sale', 'sale_share']),
                );
                const eoqSlim = _map(eoqData, (row) =>
                    _pick(row, [
                        'pid',
                        'annual_demand',
                        'unit_value',
                        'annual_holding_cost_per_unit',
                        'eoq',
                        'eoq_units',
                        'orders_per_year',
                        'order_cycle_days',
                        'total_annual_variable_cost',
                    ]),
                );

                // Left-join the analytics onto the visible inventory rows by
                // pid. The lambdas also cover hidden/delisted products (abc
                // classifies the whole catalog; rop/eoq run with filter {}),
                // so a plain union would invent rows with no sku/title.
                const abcByPid = _keyBy(abcSlim, 'pid');
                const eoqByPid = _keyBy(eoqSlim, 'pid');
                // Rows are { _id: pid, avg_stock, snapshot_days }; merging
                // _id back onto the inventory row is a no-op (same pid).
                const avgStockByPid = _keyBy(avgStockData, '_id');
                // rop lookup maps align 1:1 with ropServiceLevels; each
                // product reads the map for its class's service level.
                const ropMaps = _map(ropResponses, (response) =>
                    _keyBy(response.reordering_result, '_id'),
                );
                const ropForClass = (abcClass?: string) => {
                    let serviceLevel = this.serviceLevelOther;
                    if (abcClass === 'A') {
                        serviceLevel = this.serviceLevelA;
                    } else if (abcClass === 'B') {
                        serviceLevel = this.serviceLevelB;
                    }
                    return ropMaps[ropServiceLevels.indexOf(serviceLevel)];
                };
                const results = inventoryData.map((item: any) => {
                    const abcRow = abcByPid[item._id];
                    return _merge(
                        item,
                        ropForClass(_get(abcRow, 'class'))[item._id],
                        abcRow,
                        eoqByPid[item._id],
                        avgStockByPid[item._id],
                        {
                            supplier:
                                this.supplierIndex.byPid[item._id] ?? null,
                        },
                    );
                });
                return results;
            }),
        );
    }

    // Ordering works without 1688 data, so losing it is reported, not fatal.
    private reportAndUse<T>(
        message: string,
        fallback: T,
    ): OperatorFunction<T, T> {
        return catchError((err: unknown) => {
            this.notifyError(
                `${message}: ${this.utilService.errorToString(err)}`,
            );
            return of(fallback);
        });
    }

    onCellPrepared(e: any): void {
        if (
            e.rowType === 'header' &&
            this.columnDescriptions[e.column?.dataField]
        ) {
            e.cellElement.classList.add('has-header-tip');
        }
        if (e.rowType === 'data') {
            if (e.column.dataField === 'remain') {
                // Same coding in both modes: amber = raw on-hand shortfall
                // (regardless of what's on order), blue edge marker = has
                // open on-order qty. So untouched = amber, (partially)
                // ordered = amber + edge; fully covered rows are hidden in
                // ordering mode only.
                if (this.isBelowRop(e.data)) {
                    e.cellElement.classList.add('partial-covered');
                }
                if ((e.data.on_order_qty ?? 0) > 0) {
                    e.cellElement.classList.add('has-on-order');
                }
            }
            // Accent-blue fill on quantity cells holding a value: a typed
            // Order Qty while building an order, and On Order units backed
            // by an open PO.
            if (
                (e.column.dataField === 'order_qty' &&
                    Number(e.data.order_qty) > 0) ||
                (e.column.dataField === 'on_order_qty' &&
                    (e.data.on_order_qty ?? 0) > 0)
            ) {
                e.cellElement.classList.add('has-qty');
            }
            if (_includes(['rop', 'projected_total'], e.column.dataField)) {
                e.cellElement.classList.add('calculated-field');
            }
            if (
                _includes(['sku', 'title'], e.column.dataField) &&
                e.data.isDesisted
            ) {
                e.cellElement.classList.add('desisted-field');
            }
            // Amber corner triangle marks a manually overridden ROP.
            if (e.column.dataField === 'rop' && e.data.rop_overridden) {
                e.cellElement.classList.add('rop-overridden');
            }
        }
    }

    private prepareReorderingData(records: any[]) {
        // eoq_units is intentionally NOT zero-filled: when the lambda can't
        // compute it (insufficient data / no usable price) it stays null so
        // the grid shows a blank cell rather than a misleading 0. rop IS
        // zero-filled — no sale in the window means "reorder at 0".
        const calFields = ['unit_sold', 'data_days', 'total_sale', 'rop'];
        const classCounts: Record<string, number> = {};
        this.desistedItemCount = 0;
        _each(records, (dataItem) => {
            // Fill isDesisted flag
            if (dataItem.stock_status === 'discontinued') {
                dataItem.isDesisted = true;
                this.desistedItemCount++;
            } else {
                dataItem.isDesisted = false;
            }

            // Fill empty calculation value
            _each(calFields, (field) => {
                if (_isNil(_get(dataItem, field))) {
                    _set(dataItem, field, 0);
                }
            });

            // Units already on open purchase orders (0 until the orders doc
            // has loaded; re-applied by applyOnOrderToRows afterwards).
            dataItem.on_order_qty = this.onOrderQtyByPid[dataItem._id] ?? 0;

            // ROP override: keep the lambda value for reference, surface the
            // user's override as the effective rop everywhere.
            dataItem.rop_calculated = dataItem.rop;
            const ropOverride = this.ropOverrides[dataItem._id];
            if (!_isNil(ropOverride)) {
                dataItem.rop = ropOverride;
                dataItem.rop_overridden = true;
            } else {
                dataItem.rop_overridden = false;
            }

            // Expected demand over demandForDays = avg daily demand ×
            // demandForDays, where avg daily demand = units sold / effective
            // selling span (data_days).
            const avgSalePerDay =
                dataItem.data_days > 0
                    ? dataItem.unit_sold / dataItem.data_days
                    : 0;
            dataItem.demand = avgSalePerDay * this.demandForDays;

            // Stock cover asks "how long will what I have NOW last", so it
            // stays on today's stock. Turnover divides by the average stock
            // over the calc window (daily snapshots, merged in as avg_stock);
            // products with no snapshot rows yet fall back to today's value.
            // Null marks the combinations where the ratio is meaningless, so
            // the cell stays blank instead of showing 0 or Infinity.
            const remainNow = dataItem.remain ?? 0;
            dataItem.stock_cover_days =
                remainNow <= 0
                    ? 0
                    : avgSalePerDay > 0
                      ? remainNow / avgSalePerDay
                      : null;
            const avgStock = _isNil(dataItem.avg_stock)
                ? remainNow
                : dataItem.avg_stock;
            dataItem.avg_stock = avgStock;
            dataItem.turnover =
                avgStock > 0 ? (avgSalePerDay * 365) / avgStock : null;

            // Accumulate per-class counts for the ABC tooltip.
            if (!_isNil(dataItem.class)) {
                classCounts[dataItem.class] =
                    (classCounts[dataItem.class] ?? 0) + 1;
            }

            return dataItem;
        });

        const totalItems = records.length;
        this.classStats = {};
        _each(classCounts, (count, abcClass) => {
            this.classStats[abcClass] = {
                count,
                itemPct: totalItems > 0 ? (count / totalItems) * 100 : 0,
            };
        });
    }

    // A row is "low stock" when it is out of stock (remain <= 0) — always, even
    // if the lambda couldn't compute a ROP — or when on-hand stock has dropped
    // to/below a known ROP.
    private isBelowRop(item: any): boolean {
        return (
            item.remain <= 0 || (!_isNil(item.rop) && item.remain <= item.rop)
        );
    }

    private countBelowRopItem() {
        this.belowRopItemCount = 0;
        _each(this.dataSource, (dataItem: any) => {
            const belowRop = this.isBelowRop(dataItem);
            if (this.hideDesisted) {
                if (!dataItem.isDesisted && belowRop) {
                    this.belowRopItemCount++;
                }
            } else {
                if (belowRop) {
                    this.belowRopItemCount++;
                }
            }
        });
    }

    // Null for a product the crawler has never stamped — moment(undefined)
    // is "now", which would show never-crawled stock as just updated.
    convertLastUpdateDatetimeToMoment(data: any): Moment | null {
        return _isNil(data.crawler_last_updated)
            ? null
            : moment(data.crawler_last_updated);
    }

    // A row that stopped needing ordering (covered by a PO edit, lowered ROP
    // override, pause) must not keep a draft qty — it would silently ship
    // with the next saved order while invisible.
    private dropCoveredDrafts(): void {
        let dropped = false;
        _each(this.dataSource, (row: any) => {
            if (!_isNil(row.order_qty) && !this.needsOrdering(row)) {
                delete row.order_qty;
                dropped = true;
            }
        });
        if (dropped) {
            this.recountTypedLines();
        }
    }

    onFilterSelectionChanged() {
        // Any pass satisfies one that was parked behind an open editor.
        this.refilterPending = false;
        const filters = [];
        // Skipped in ordering mode, where the button is disabled to match:
        // needsOrdering is strictly narrower (it adds on-order stock, which
        // only raises the effective figure), so ANDing this removes nothing.
        // It is not merely redundant either — needsOrdering reads `remain ?? 0`
        // while isBelowRop reads it raw, so a row with no `remain` value is
        // shown by ordering mode and hidden by this filter. The user's choice
        // is left untouched so it applies again on exit.
        if (this.showOnlyBelowRop && !this.orderingMode) {
            filters.push((itemData: any) => this.isBelowRop(itemData));
        }
        if (this.hideDesisted || !_isEmpty(this.selectedClassFilters)) {
            if (!_isEmpty(filters)) {
                filters.push('and');
            }
            filters.push((itemData: any) =>
                this.passesToolbarFilters(itemData),
            );
        }
        if (this.orderingMode) {
            this.dropCoveredDrafts();
            if (!_isEmpty(filters)) {
                filters.push('and');
            }
            filters.push((itemData: any) => this.needsOrdering(itemData));
            this.rebuildSupplierShopItems();
            const shop = this.selectedSupplierShop;
            if (shop) {
                filters.push('and');
                filters.push(
                    (itemData: any) =>
                        (itemData.supplier?.shopUrl ?? NO_SUPPLIER_KEY) ===
                        shop,
                );
            }
        }
        // One filter() call (null clears): unlike clearFilter(), this leaves
        // the user's filter-row entries intact and avoids a double pass.
        this.dataGrid.instance.filter(_isEmpty(filters) ? null : filters);
        this.countBelowRopItem();
    }

    private passesToolbarFilters(row: any): boolean {
        return (
            !(this.hideDesisted && row.isDesisted) &&
            (_isEmpty(this.selectedClassFilters) ||
                _includes(this.selectedClassFilters, row.class))
        );
    }

    // The shops of what the ordering table would show with no shop picked, most
    // to buy first. A picked shop stays listed once nothing is left to buy from
    // it, so the box doesn't go blank with the filter still on.
    private rebuildSupplierShopItems(): void {
        const rows = _filter(
            this.dataSource,
            (row: any) =>
                this.needsOrdering(row) && this.passesToolbarFilters(row),
        );
        const counts = _countBy(
            rows,
            (row: any) => row.supplier?.shopUrl ?? NO_SUPPLIER_KEY,
        );
        const selected = this.selectedSupplierShop;
        if (selected && !counts[selected]) {
            counts[selected] = 0;
        }
        const nameOf = (key: string): string =>
            key === NO_SUPPLIER_KEY
                ? 'No 1688 data'
                : (this.supplierIndex.shops.find((shop) => shop.url === key)
                      ?.name ?? key);
        const items = _orderBy(
            _map(counts, (count, key) => ({ key, count, name: nameOf(key) })),
            [(item) => (item.key === NO_SUPPLIER_KEY ? 1 : 0), 'count', 'name'],
            ['asc', 'desc', 'asc'],
        ).map(({ key, count, name }) => ({
            value: key,
            text: `${name} (${count})`,
        }));
        // Every filter pass lands here; the box only hears of a real change.
        if (!_isEqual(items, this.supplierShopItems)) {
            this.supplierShopItems = items;
        }
    }

    exportExcel(): void {
        if (this.exporting) {
            return;
        }
        this.exporting = true;
        const workbook = new Workbook();
        const sheet = workbook.addWorksheet('Reordering');
        exportDataGrid({
            component: this.dataGrid.instance,
            worksheet: sheet,
            autoFilterEnabled: true,
            customizeCell: ({ gridCell, excelCell }) => {
                if (_get(gridCell, 'rowType') !== 'data') {
                    return;
                }
                const dataField = _get(gridCell, 'column.dataField', '');
                // The grid shows this via a Moment-based template; hand
                // Excel a real Date so it sorts/formats natively.
                if (dataField === 'crawler_last_updated') {
                    // Never crawled stays blank, as on screen.
                    if (!_isNil(gridCell?.value)) {
                        excelCell.value = moment(gridCell?.value).toDate();
                        excelCell.numFmt = 'yyyy-mm-dd hh:mm';
                    }
                    return;
                }
                const numFmt = EXPORT_NUM_FORMATS[dataField];
                if (_isNil(numFmt) || _isNil(gridCell?.value)) {
                    return;
                }
                // Decimal128 fields arrive as strings — coerce so the
                // number format actually applies.
                if (typeof gridCell.value === 'string') {
                    const parsed = Number(gridCell.value);
                    if (!Number.isNaN(parsed)) {
                        excelCell.value = parsed;
                    }
                }
                excelCell.numFmt = numFmt;
            },
        })
            .then(() => {
                // Keep the header row visible while scrolling in Excel.
                sheet.views = [{ state: 'frozen', ySplit: 1 }];
                return workbook.xlsx.writeBuffer();
            })
            .then((buffer: BlobPart) => {
                FileSaver.saveAs(
                    new Blob([buffer], {
                        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    }),
                    `Reordering ${moment().format('YYYY_MM_DD-HH_mm_ss')}.xlsx`,
                );
            })
            .catch((err) => {
                console.error(err);
                this.notificationService.pushMessage({
                    displayTime: 6000,
                    message: this.utilService.errorToString(err),
                    messageType: MessageType.error,
                });
            })
            .finally(() => {
                this.exporting = false;
            });
    }
}
